import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, Building2, BanknoteIcon, UserRound, ArrowRight, Star, Zap, Shield, Clock, Gift, ChevronDown, Info } from 'lucide-react';

const bankData = {
  akbank: {
    name: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
    color: 'red',
    products: {
      credits: [
        {
          title: 'İhtiyaç Kredisi',
          rate: '3.99',
          term: '36',
          amount: '500.000',
          features: ['Masrafsız kullanım', 'Anında onay', '3 ay erteleme']
        },
        {
          title: 'Konut Kredisi',
          rate: '2.99',
          term: '120',
          amount: '10.000.000',
          features: ['%90 finansman', 'Ekspertiz indirimi', 'Masraf avantajı']
        }
      ],
      cards: [
        {
          title: 'Axess',
          type: 'Kredi Kartı',
          features: ['Chip-para kazanımı', 'Taksit avantajları', 'Ücretsiz ek kart']
        },
        {
          title: 'Wings',
          type: 'Kredi Kartı',
          features: ['Mil kazanımı', 'Lounge erişimi', 'Seyahat sigortası']
        }
      ],
      deposits: [
        {
          title: 'e-Mevduat',
          rate: '45',
          term: '32',
          features: ['Günlük faiz', 'Vade sonu bekleme yok', 'Online işlem']
        }
      ]
    },
    campaigns: [
      {
        title: 'Yeni Müşteri Kampanyası',
        description: 'İlk kredi kartınıza 1.000 TL chip-para hediye!',
        endDate: '31.12.2024'
      },
      {
        title: 'Hoş Geldin Kredisi',
        description: '%0 faizli 10.000 TL kredi fırsatı',
        endDate: '31.12.2024'
      }
    ]
  },
  // Add other banks here...
};

const BankPage = () => {
  const { bankId } = useParams<{ bankId: string }>();
  const [activeTab, setActiveTab] = useState<'credits' | 'cards' | 'deposits'>('credits');
  const bank = bankData[bankId as keyof typeof bankData];

  if (!bank) {
    return <div>Banka bulunamadı</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-6 mb-8">
              <img 
                src={bank.logo} 
                alt={bank.name}
                className="h-16 object-contain"
              />
              <div>
                <h1 className="text-3xl font-bold text-slate-800 mb-2">
                  {bank.name} Ürün ve Kampanyaları
                </h1>
                <p className="text-slate-600">
                  En avantajlı {bank.name} ürünlerini inceleyin, size en uygun olanı seçin
                </p>
              </div>
            </div>

            {/* Campaigns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bank.campaigns.map((campaign, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 border border-slate-200 hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-[#F7BA45]/10 p-3 rounded-lg">
                      {index === 0 ? (
                        <Gift className="h-6 w-6 text-[#F7BA45]" />
                      ) : (
                        <Zap className="h-6 w-6 text-[#F7BA45]" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">{campaign.title}</h3>
                      <p className="text-slate-600 text-sm mb-3">{campaign.description}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock size={14} />
                        <span>Son Geçerlilik: {campaign.endDate}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Tabs */}
            <div className="flex items-center gap-4 mb-8 border-b border-slate-200">
              <button
                onClick={() => setActiveTab('credits')}
                className={`px-6 py-4 font-medium transition-colors relative ${
                  activeTab === 'credits' 
                    ? 'text-[#F7BA45]' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Krediler
                {activeTab === 'credits' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F7BA45]" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('cards')}
                className={`px-6 py-4 font-medium transition-colors relative ${
                  activeTab === 'cards' 
                    ? 'text-[#F7BA45]' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Kredi Kartları
                {activeTab === 'cards' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F7BA45]" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('deposits')}
                className={`px-6 py-4 font-medium transition-colors relative ${
                  activeTab === 'deposits' 
                    ? 'text-[#F7BA45]' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Mevduat
                {activeTab === 'deposits' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F7BA45]" />
                )}
              </button>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeTab === 'credits' && bank.products.credits.map((credit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-6">{credit.title}</h3>
                    
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-sm text-slate-500 mb-1">Faiz Oranı</div>
                        <div className="font-semibold text-slate-800">%{credit.rate}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-sm text-slate-500 mb-1">Vade</div>
                        <div className="font-semibold text-slate-800">{credit.term} Ay</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-sm text-slate-500 mb-1">Tutar</div>
                        <div className="font-semibold text-slate-800">{credit.amount} TL</div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      {credit.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <Shield size={16} className="text-[#F7BA45]" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg">
                      HEMEN BAŞVUR
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              ))}

              {activeTab === 'cards' && bank.products.cards.map((card, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">{card.title}</h3>
                    <p className="text-sm text-slate-600 mb-6">{card.type}</p>
                    
                    <div className="space-y-3 mb-6">
                      {card.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <Star size={16} className="text-[#F7BA45]" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg">
                      HEMEN BAŞVUR
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              ))}

              {activeTab === 'deposits' && bank.products.deposits.map((deposit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-6">{deposit.title}</h3>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-sm text-slate-500 mb-1">Faiz Oranı</div>
                        <div className="font-semibold text-slate-800">%{deposit.rate}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-sm text-slate-500 mb-1">Vade</div>
                        <div className="font-semibold text-slate-800">{deposit.term} Gün</div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      {deposit.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <Shield size={16} className="text-[#F7BA45]" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg">
                      HEMEN BAŞVUR
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankPage;